
    class PopupDialog extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.sectionId = this.dataset.sectionId;
        this.storageKey = `popup-dismissed:${this.sectionId}`;
        this.editorKey = `popup-editing:${this.sectionId}`;
        this.cleanup = [];
        this.triggerCleanup = [];

        this.on(this, 'click', (event) => {
          if (event.target.closest('[data-popup-close]')) this.close();
        });

        // Clicking the backdrop lands on the <dialog> element itself
        this.on(this.dialog, 'click', (event) => {
          if (event.target === this.dialog && this.dataset.overlayClose === 'true') this.close();
        });

        this.on(this.dialog, 'cancel', (event) => {
          event.preventDefault();
          this.close();
        });

        if (window.Shopify && Shopify.designMode) {
          this.setupEditor();
          return;
        }

        if (this.dataset.enabled !== 'true') return;

        // Reopen after a form inside the popup was submitted (e.g. newsletter success message)
        if (this.hasPostedForm()) {
          this.open(false);
          return;
        }

        if (!this.isScheduled() || !this.matchesDevice() || this.wasDismissed()) return;

        this.setupTriggers();
      }

      disconnectedCallback() {
        this.cleanup?.forEach((fn) => fn());
        this.clearTriggers();
      }

      clearTriggers() {
        this.triggerCleanup?.forEach((fn) => fn());
        this.triggerCleanup = [];
        clearTimeout(this.timer);
      }

      onTrigger(target, type, handler, options) {
        target.addEventListener(type, handler, options);
        this.triggerCleanup.push(() => target.removeEventListener(type, handler, options));
      }

      on(target, type, handler, options) {
        target.addEventListener(type, handler, options);
        this.cleanup.push(() => target.removeEventListener(type, handler, options));
      }

      setupTriggers() {
        const trigger = this.dataset.trigger;
        const isTouch = window.matchMedia('(hover: none)').matches;

        if (trigger === 'delay' || trigger === 'delay_or_scroll' || (trigger === 'exit_intent' && isTouch)) {
          this.timer = setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
        }

        if (trigger === 'scroll' || trigger === 'delay_or_scroll') {
          const target = Number(this.dataset.scroll);
          const check = () => {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const percent = max > 0 ? (window.scrollY / max) * 100 : 100;
            if (percent >= target) this.open();
          };
          this.onTrigger(window, 'scroll', check, { passive: true });
          check();
        }

        if (trigger === 'exit_intent' && !isTouch) {
          this.onTrigger(document, 'mouseout', (event) => {
            if (!event.relatedTarget && event.clientY <= 0) this.open();
          });
        }
      }

      setupEditor() {
        const isThis = (event) => event.detail.sectionId === this.sectionId;

        this.on(document, 'shopify:section:select', (event) => {
          if (!isThis(event)) return;
          sessionStorage.setItem(this.editorKey, '1');
          this.open(false);
        });
        this.on(document, 'shopify:block:select', (event) => {
          if (isThis(event)) this.open(false);
        });
        this.on(document, 'shopify:section:deselect', (event) => {
          if (!isThis(event)) return;
          sessionStorage.removeItem(this.editorKey);
          this.close(false);
        });

        // The section re-renders on every setting change; keep it open while editing
        if (sessionStorage.getItem(this.editorKey)) this.open(false);
      }

      open(track = true) {
        if (this.dialog.open) return;
        this.clearTriggers();

        this.dialog.classList.remove('is-closing');
        if (this.dataset.modal === 'true') {
          this.dialog.showModal();
        } else {
          this.dialog.show();
        }
        this.track = track;
      }

      close(track = true) {
        if (!this.dialog.open) return;
        if (track && this.track !== false) this.rememberDismissal();

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) {
          this.dialog.close();
          return;
        }
        this.dialog.classList.add('is-closing');
        this.dialog.addEventListener(
          'animationend',
          () => {
            this.dialog.classList.remove('is-closing');
            this.dialog.close();
          },
          { once: true }
        );
      }

      // Dates are entered in the store's timezone: "YYYY-MM-DD" or "YYYY-MM-DD HH:MM"
      parseDate(value, endOfDay) {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
        if (!match) {
          if (value) console.warn(`Popup: invalid date "${value}", expected YYYY-MM-DD or YYYY-MM-DD HH:MM`);
          return null;
        }
        const [, year, month, day, hour, minute] = match;
        const time = hour ? `${hour.padStart(2, '0')}:${minute}:00` : endOfDay ? '23:59:59' : '00:00:00';
        const offset = (this.dataset.timezoneOffset || '+0000').replace(/(\d{2})(\d{2})$/, '$1:$2');
        const date = new Date(`${year}-${month}-${day}T${time}${offset}`);
        return isNaN(date) ? null : date;
      }

      isScheduled() {
        const now = Date.now();
        const start = this.parseDate(this.dataset.start, false);
        const end = this.parseDate(this.dataset.end, true);
        if (start && now < start) return false;
        if (end && now > end) return false;
        return true;
      }

      matchesDevice() {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        return isMobile ? this.dataset.mobile === 'true' : this.dataset.desktop === 'true';
      }

      wasDismissed() {
        const frequency = this.dataset.frequency;
        try {
          if (frequency === 'session') return sessionStorage.getItem(this.storageKey) === '1';
          if (frequency === 'days') {
            const until = Number(localStorage.getItem(this.storageKey));
            return until > Date.now();
          }
        } catch (e) {}
        return false;
      }

      rememberDismissal() {
        const frequency = this.dataset.frequency;
        try {
          if (frequency === 'session') sessionStorage.setItem(this.storageKey, '1');
          if (frequency === 'days') {
            const days = Number(this.dataset.frequencyDays) || 1;
            localStorage.setItem(this.storageKey, String(Date.now() + days * 86400000));
          }
        } catch (e) {}
      }

      hasPostedForm() {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('customer_posted') && !params.has('contact_posted')) return false;
        const hash = window.location.hash.slice(1);
        if (hash && this.dialog.querySelector(`[id="${CSS.escape(hash)}"]`)) return true;
        return !!this.dialog.querySelector('form .form__message, form [role="alert"], form .errors');
      }
    }
  if (!customElements.get('popup-dialog')) {
    customElements.define('popup-dialog', PopupDialog);
  }